import { useCallback } from 'react';
import uuid from 'react-uuid';
import { ApiService, CapTableFundOwnership, CapTablesService, FundOwnershipDetailWithChildren, ProFormaCapTableModel } from 'api';
import { PREFERRED_STOCK } from 'common/constants/securityTypes';
import { NewRoundModelerData } from 'pages/CapTable/cap-table/components/proforma-captable-dialog/NewRoundModeler.props';
import { CaptableColumn } from '../CaptableTable.props';

interface UseAfterProformaCreateParams {
  nrmState: NewRoundModelerData | null | undefined;
  companyInfo: { id?: number } | null | undefined;
}

// CapTableFundOwnership (generated from the read serializer) omits fund_ownership_detail,
// which the write endpoint accepts. Extend it locally instead of typing the payload as `any`.
// The write endpoint (backend FundOwnershipDetailSerializer) also has a stricter contract than
// the read-shaped FundOwnershipDetailWithChildren: `id` (0 for new, matching the id==0 "new
// record" convention used across cap-table save payloads) is required, and `acquisitions` must
// be omitted entirely rather than sent as `[]` (allow_empty=False on that field).
type FundOwnershipDetailWritePayload = Omit<FundOwnershipDetailWithChildren, 'id' | 'acquisitions'> & {
  id: number;
  acquisitions?: Array<FundOwnershipDetailWithChildren['acquisitions'][number]>;
};

type FundOwnershipUpdatePayload = CapTableFundOwnership & {
  fund_ownership_detail: Array<FundOwnershipDetailWritePayload>;
};

// Existing fund-ownership-detail rows copied from the primary cap table always carry an
// `acquisitions` array (possibly empty) per the read contract; re-sending an empty one on this
// endpoint fails validation, so strip the key rather than send `[]`.
const sanitizeFodForWrite = (fod: FundOwnershipDetailWithChildren): FundOwnershipDetailWritePayload => {
  if (fod.acquisitions.length > 0) return fod as FundOwnershipDetailWritePayload;
  const { acquisitions, ...rest } = fod;
  return rest as FundOwnershipDetailWritePayload;
};

const useAfterProformaCreate = ({ nrmState, companyInfo }: UseAfterProformaCreateParams) => {
  const isActive = !!(nrmState?.isEnabled && nrmState?.isValid && nrmState?.computed);

  const buildColumnsForProforma = useCallback(
    (activeColumns: CaptableColumn[]): CaptableColumn[] => {
      if (!isActive || !nrmState?.computed) return activeColumns;

      const preferredSecurities = activeColumns.filter(
        c => c.security_type === PREFERRED_STOCK && !c.is_deleted
      );
      const sortedByDate = [...preferredSecurities]
        .filter(c => c.investment_date?.trim())
        .sort((a, b) => new Date(b.investment_date!).getTime() - new Date(a.investment_date!).getTime());
      const sourcePreferred = sortedByDate[0] ?? preferredSecurities[0];
      if (!sourcePreferred) return activeColumns;

      const newSecurity: CaptableColumn = {
        ...sourcePreferred,
        id: 0,
        columnRef: uuid(),
        security_ref: null,
        underlying_security_ref: null,
        name: nrmState.securityName,
        shares_outstanding: String(Math.round(nrmState.computed.newRoundSharesIssued)),
        issue_price: nrmState.computed.newRoundIssuePrice.toFixed(15),
        investment_date: nrmState.investmentDate || null,
        seniority: (sourcePreferred.seniority ?? 0) + 1,
      };
      return [...activeColumns, newSecurity];
    },
    [isActive, nrmState]
  );

  const afterCreate = useCallback(
    async (newCap: ProFormaCapTableModel) => {
      if (!nrmState?.computed) return;
      const newCapId = newCap.id;
      const { computed, securityName, fundId, fundInvestment, investmentDate } = nrmState;
      const capturedCompanyId = companyInfo?.id;

      let copiedFods: FundOwnershipDetailWithChildren[] = [];
      let fundOwnershipId: number | undefined;
      try {
        const copyResult = await ApiService.apiCapTablesFundOwnershipCopyFromPrimaryCreate(newCapId);
        copiedFods = copyResult.fund_ownership_detail ?? [];
        fundOwnershipId = copyResult.id;
      } catch {
        try {
          const fo = await ApiService.apiCapTablesFundOwnershipRead(newCapId);
          fundOwnershipId = fo.id;
        } catch (err) {
          console.error('Failed to read fund ownership for proforma:', err);
        }
      }

      if (!fundInvestment || fundInvestment <= 0) {
        if (copiedFods.length > 0) {
          const payload: FundOwnershipUpdatePayload = {
            cap_table: newCapId,
            fund_ownership_detail: copiedFods.map(sanitizeFodForWrite),
          };
          await ApiService.apiCapTablesFundOwnershipUpdate(newCapId, payload);
        }
        return;
      }

      let newSecurityId: number | undefined;
      try {
        const proformaData = await CapTablesService.apiCapTablesRead(newCapId);
        const securities = proformaData?.securities ?? [];
        newSecurityId = securities.find(s => s.name === securityName)?.id ?? undefined;
        if (!fundOwnershipId && proformaData?.fund_ownership?.id) {
          fundOwnershipId = proformaData.fund_ownership.id;
        }
      } catch (err) {
        console.error('Failed to fetch proforma cap table after create:', err);
      }

      // Both ids are guaranteed by the API contract at this point (a freshly created proforma
      // always has a fund ownership record, and companyInfo is always present while this dialog
      // is open) — bail instead of sending a payload with missing required fields.
      if (newSecurityId == null || fundOwnershipId == null || capturedCompanyId == null) return;

      // id: 0 marks this as a new row (same convention as securities); acquisitions is
      // intentionally omitted — the backend rejects an empty acquisitions list outright, and
      // this FOD's shares/invested_capital are the source of truth, not a purchase ledger.
      const newFod: FundOwnershipDetailWritePayload = {
        id: 0,
        is_deleted: false,
        fund_ownership: fundOwnershipId,
        security: newSecurityId,
        fund: fundId,
        shares: String(Math.round(computed.numberOfShares)),
        invested_capital: String(fundInvestment),
        investment_date: investmentDate || undefined,
        entity_reference: capturedCompanyId,
        order: copiedFods.length,
        custom_security_name: '',
        loan_value: '0',
      };

      const payload: FundOwnershipUpdatePayload = {
        id: fundOwnershipId,
        cap_table: newCapId,
        fund_ownership_detail: [...copiedFods.map(sanitizeFodForWrite), newFod],
      };
      await ApiService.apiCapTablesFundOwnershipUpdate(newCapId, payload);
    },
    [nrmState, companyInfo]
  );

  return {
    buildColumnsForProforma,
    afterCreate: isActive ? afterCreate : undefined,
  };
};

export default useAfterProformaCreate;
